import { ReviewTrace, ReviewVerdict } from '../../../api/structs';
import { ReviewPolicyOutcome } from './graphTypes';

/** The tiers whose last outcome the core node badges. */
export const REVIEW_BADGE_TIERS = ['check', 'confirm', 'pick', 'pair'];

/**
 * The verdict-metadata stamps the engine writes AFTER a lever has fired or
 * been refused (`closing_frame_drop.go:88`, `attachment_rewire_apply.go:103`,
 * `review_video_prompt.go:39-43`). Later entries win, so a refusal recorded
 * after an application reads as the refusal.
 *
 * Every key here is a RESULT. A judge's PROPOSAL is not one, and none may be
 * added: `metadata.reconcile` and `metadata.rewire` are what the confirm tier
 * ASKED for (`review_reconcile.go:63`, `attachment_rewire.go:94`), and the
 * engine rejects an empty or echoed request and has five further ways to
 * rewrite nothing at all. Reading a proposal as an outcome would badge a run
 * `fired` for something that never happened — and because these stamps are
 * applied last, it would also overwrite the tier evidence that says so.
 */
const POLICY_STAMPS: { key: string; policy: string; outcome: ReviewPolicyOutcome }[] = [
  { key: 'rewire_applied', policy: 'rewire', outcome: 'fired' },
  { key: 'drop_closing_frame_applied', policy: 'drop_closing_frame', outcome: 'fired' },
  { key: 'drop_closing_frame_ignored', policy: 'drop_closing_frame', outcome: 'skipped' },
  { key: 'video_prompt_adjusted', policy: 'adjust_video_prompt', outcome: 'fired' },
  { key: 'video_prompt_adjustment_ignored', policy: 'adjust_video_prompt', outcome: 'skipped' },
];

export interface ReviewRunEvidence {
  /** Last outcome per badge tier. */
  tierOutcomes: Record<string, string>;
  /** Policy id → what the run says happened. Absent = the run says nothing. */
  policies: Record<string, ReviewPolicyOutcome>;
}

/**
 * What ONE RUN's own record says the reviewer did, as opposed to what it is
 * allowed to do. Two sources, deliberately:
 *
 * - TRACES are one row per judge call, so they carry the tier outcomes. Two
 *   tiers are also lever evidence in themselves, because the engine only makes
 *   the call once the lever is really running: a `keeps` call happens only
 *   because the video-prompt rewrite is being checked, and a `reconcile` call
 *   only because the later briefs are being rewritten.
 * - Some levers' RESULTS are stamped on the stored VERDICTS — a trace cannot
 *   say whether a proposal was then applied or refused.
 *
 * Where both speak, the stamps win: a keeps check that passed still means
 * nothing if the adjustment was refused for a reason the stamp names. Where
 * only a proposal exists, NEITHER speaks and the policy carries no badge.
 */
export function reviewRunEvidence(
  traces?: ReviewTrace[],
  verdicts?: ReviewVerdict[]
): ReviewRunEvidence {
  const tierOutcomes: Record<string, string> = {};
  const policies: Record<string, ReviewPolicyOutcome> = {};

  (traces || []).forEach((trace) => {
    if (REVIEW_BADGE_TIERS.includes(trace.tier)) {
      tierOutcomes[trace.tier] = trace.outcome;
      return;
    }
    if (trace.tier === 'keeps') {
      policies.adjust_video_prompt = trace.outcome === 'pass' ? 'fired' : 'skipped';
      return;
    }
    if (trace.tier === 'reconcile') policies.reconcile = 'fired';
  });

  (verdicts || []).forEach((verdict) => {
    const metadata = verdict.metadata || {};
    POLICY_STAMPS.forEach((stamp) => {
      if (metadata[stamp.key] !== undefined) policies[stamp.policy] = stamp.outcome;
    });
  });

  return { tierOutcomes, policies };
}

export default reviewRunEvidence;
